import React, { Fragment, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import styled from 'styled-components';
import _ from 'lodash';

import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';

import { Button } from 'components/core';
import { HorizontalDivider } from 'components/core/divider';
import { DesktopOnly, SmallLoader } from 'shared/components';
import EmptyLoadingState from 'components/empty-loading-state';
import { ARIA_ROLES } from 'shared/constants';
import { ORDER_STATUSES } from 'src/constants';

import { useModals, ModalNames } from 'components/modals';
import { useReorder } from 'hooks/use-reorder';
import { ConsumerOrder, ConsumerUser } from 'components/embedded-account/account.types';
import { ListContainer, TabHeaderTitle } from '../components';

import OrderListItem from './order-list-item';

const { OPEN, CONFIRMED, IN_TRANSIT } = ORDER_STATUSES;

type OrderListProps = {
  handleLoadMore: () => void;
  hasMoreOrders: boolean;
  loading: boolean;
  orders: ConsumerOrder[];
  user: ConsumerUser;
};

const OrderList = ({ handleLoadMore, hasMoreOrders, loading, orders, user }: OrderListProps): JSX.Element => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const UI = useUI();
  const router = useRouter();
  const { orderId } = router?.query ?? {};
  const { reorder, loadingOrderId } = useReorder();

  useEffect(() => {
    if (orderId) {
      user.viewOrderId = orderId;
      void openModal(ModalNames.orderStatus);
    }
  }, [orderId, user, openModal]);

  const handleViewOrder = (id: string, canViewStatus: boolean): void => {
    user.viewOrderId = id;

    if (canViewStatus) {
      void UI.showOrder(id, true);
    } else {
      void openModal(ModalNames.orderReceipt);
    }
  };

  const showLoadingSpinner = loading || !orders;

  return (
    <OrdersContainer>
      <DesktopOnly>
        <TabHeaderTitle tag='h2' size='medium'>
          {t('accountsPage.orders', 'Orders')}
        </TabHeaderTitle>
      </DesktopOnly>

      <OrdersListContainer>
        {_.map(orders, (order, index) => {
          const { status } = order;
          const canViewStatus = _.includes([OPEN, CONFIRMED, IN_TRANSIT], status);

          return (
            <Fragment key={order.orderId}>
              <OrderListItem
                order={order}
                canViewStatus={canViewStatus}
                onViewOrder={() => handleViewOrder(order.orderId ?? '', canViewStatus)}
                onReorder={reorder ? () => void reorder(order, { source: 'order list' }) : undefined}
                reorderLoading={loadingOrderId === order.orderId}
              />
              {index < orders.length - 1 && <OrdersListDivider mt={24} mb={24} />}
            </Fragment>
          );
        })}

        {showLoadingSpinner ? (
          <SmallLoader />
        ) : (
          <LoadMoreButton
            variant='secondary'
            role={ARIA_ROLES.BUTTON}
            aria-label={t('accountsPage.loadMore', 'Load More')}
            onClick={handleLoadMore}
            show={hasMoreOrders}
          >
            {t('accountsPage.loadMore', 'Load More')}
          </LoadMoreButton>
        )}

        {!loading && orders.length === 0 && <EmptyLoadingState page='orders' isEmpty />}
      </OrdersListContainer>
    </OrdersContainer>
  );
};

export default OrderList;

const LoadMoreButton = styled(Button)<{ show?: boolean }>`
  margin-top: 35px;
  display: ${({ show }) => (show ? `flex` : `none`)};
  justify-content: center;
  align-items: center;
  border: 2px solid var(--dct--customized-colors-buttonsLinks);
  width: 99px;
  height: 29px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: auto;
    margin-top: 24px;
  }
`;

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const OrdersListContainer = styled(ListContainer)`
  margin-top: 10px;
`;

const OrdersListDivider = styled(HorizontalDivider)`
  ${({ theme }) => theme.breakpoints.up(`md`)} {
    display: flex;
  }
`;
